import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

// ACTIONS
const calendarBookingRequested = createAction("calendar/booking/requested")
const calendarBookingReceived = createAction("calendar/booking/received")
const calendarBookingFailed = createAction("calendar/booking/failed")

const calendarTripLocationsRequested = createAction("calendar/trip_locations/requested")
const calendarTripLocationsReceived = createAction("calendar/trip_locations/received")
const calendarTripLocationsFailed = createAction("calendar/trip_locations/failed")

const calendarJournalNoteRequested = createAction("calendar/journal_note/requested")
const calendarJournalNoteReceived = createAction("calendar/journal_note/received")
const calendarJournalNoteFailed = createAction("calendar/journal_note/failed")

const calendarJournalCreateNoteRequested = createAction("calendar/journal_note/create_note/requested")
const calendarJournalCreateNoteReceived = createAction("calendar/journal_note/create_note/received")
const calendarJournalCreateNoteFailed = createAction("calendar/journal_note/create_note/failed")

const calendarJournalUpdateNoteRequested = createAction("calendar/journal_note/update_note/requested")
const calendarJournalUpdateNoteReceived = createAction("calendar/journal_note/update_note/received")
const calendarJournalUpdateNoteFailed = createAction("calendar/journal_note/update_note/failed")

const calendarJournalVersionsRequested = createAction("calendar/journal_note/versions/requested")
const calendarJournalVersionsReceived = createAction("calendar/journal_note/versions/received")
const calendarJournalVersionsFailed = createAction("calendar/journal_note/versions/failed")

const calendarTimeOffDeleteRequested = createAction("calendar/time_off/delete/requested")
const calendarTimeOffDeleteReceived = createAction("calendar/time_off/delete/received")
const calendarTimeOffDeleteFailed = createAction("calendar/time_off/delete/failed")

const calendarTimeOffCreateRequested = createAction("calendar/time_off/create/requested")
const calendarTimeOffCreateReceived = createAction("calendar/time_off/create/received")
const calendarTimeOffCreateFailed = createAction("calendar/time_off/create/failed")

const calendarTimeOffRequested = createAction("calendar/time_off/requested")
const calendarTimeOffReceived = createAction("calendar/time_off/received")
const calendarTimeOffFailed = createAction("calendar/time_off/failed")

const calendarTimeOffUpdateRequested = createAction("calendar/time_off/update/requested")
const calendarTimeOffUpdateReceived = createAction("calendar/time_off/update/received")
const calendarTimeOffUpdateFailed = createAction("calendar/time_off/update/failed")

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  bookings: [],
  daysEmpty: false,
  calendarTimeOff: [],
  journalNoteDates: [],
  tripLocations: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  journalNote: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  journalVersions: {
    ...LOADING_INITIAL_STATE,
    data: {
      versions: [],
      pagination: null
    }
  },
  timeOff: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  timeOffSave: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  timeOffUpdate: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  timeOffDelete: {
    ...LOADING_INITIAL_STATE,
    data: null
  }
}

const calendarReducer = createReducer(initialState, {
  [calendarBookingRequested.type]: (state) => {
    setRequested(state)
  },
  [calendarBookingReceived.type]: (state, action) => {
    setReceived(state)
    const data = action.payload?.data
    // Day view returns data.bookings (flat array)
    // Week/month view returns data.days[].bookings (grouped by day)
    state.bookings = data?.bookings ?? data?.days?.flatMap((d) => d.bookings ?? []) ?? []
    state.daysEmpty = Array.isArray(data?.days) && data.days.length === 0
    state.journalNoteDates = data?.journal_note_dates || []
  },
  [calendarBookingFailed.type]: (state) => {
    setFailed(state)
  },
  [calendarTripLocationsRequested.type]: (state) => {
    setRequested(state.tripLocations)
    state.tripLocations.data = []
  },
  [calendarTripLocationsReceived.type]: (state, action) => {
    setReceived(state.tripLocations)
    state.tripLocations.data = action.payload?.trip_locations || []
  },
  [calendarTripLocationsFailed.type]: (state) => {
    setFailed(state.tripLocations)
    state.tripLocations.data = []
  },
  [calendarJournalNoteRequested.type]: (state) => {
    setRequested(state.journalNote)
    state.journalNote.data = null
  },
  [calendarJournalNoteReceived.type]: (state, action) => {
    setReceived(state.journalNote)
    state.journalNote.data = action.payload || null
  },
  [calendarJournalNoteFailed.type]: (state) => {
    setFailed(state.journalNote)
    state.journalNote.data = null
  },
  [calendarJournalCreateNoteRequested.type]: (state) => {
    setRequested(state)
  },
  [calendarJournalCreateNoteReceived.type]: (state, action) => {
    setReceived(state)
  },
  [calendarJournalCreateNoteFailed.type]: (state) => {
    setFailed(state)
  },
  [calendarJournalUpdateNoteRequested.type]: (state) => {
    setRequested(state)
  },
  [calendarJournalUpdateNoteReceived.type]: (state, action) => {
    setReceived(state)
  },
  [calendarJournalUpdateNoteFailed.type]: (state) => {
    setFailed(state)
  },
  [calendarJournalVersionsRequested.type]: (state) => {
    setRequested(state.journalVersions)
  },
  [calendarJournalVersionsReceived.type]: (state, action) => {
    setReceived(state.journalVersions)
    state.journalVersions.data = {
      versions: action.payload?.versions || [],
      pagination: action.payload?.pagination || null
    }
  },
  [calendarJournalVersionsFailed.type]: (state) => {
    setFailed(state.journalVersions)
    state.journalVersions.data = {
      versions: [],
      pagination: null
    }
  },
  [calendarTimeOffDeleteRequested.type]: (state) => {
    setRequested(state.timeOffDelete)
  },
  [calendarTimeOffDeleteReceived.type]: (state) => {
    setReceived(state.timeOffDelete)
  },
  [calendarTimeOffDeleteFailed.type]: (state) => {
    setFailed(state.timeOffDelete)
  },
  [calendarTimeOffCreateRequested.type]: (state) => {
    setRequested(state.timeOffSave)
  },
  [calendarTimeOffCreateReceived.type]: (state) => {
    setReceived(state.timeOffSave)
  },
  [calendarTimeOffCreateFailed.type]: (state) => {
    setFailed(state.timeOffSave)
  },
  [calendarTimeOffRequested.type]: (state) => {
    setRequested(state.timeOff)
    state.timeOff.data = []
  },
  [calendarTimeOffReceived.type]: (state, action) => {
    setReceived(state.timeOff)
    const mapped = (action.payload || []).map((item) => ({
      ...item,
      day: item.day ?? item.unavailable_weekday,
      season_start: item.season_start ?? item.start_date,
      season_end: item.season_end ?? item.end_date,
    }))
    state.timeOff.data = mapped
    // Sync to calendarTimeOff so calendar views stay populated
    state.calendarTimeOff = mapped
  },
  [calendarTimeOffFailed.type]: (state) => {
    setFailed(state.timeOff)
    state.timeOff.data = []
  },
  [calendarTimeOffUpdateRequested.type]: (state) => {
    setRequested(state.timeOffUpdate)
  },
  [calendarTimeOffUpdateReceived.type]: (state) => {
    setReceived(state.timeOffUpdate)
  },
  [calendarTimeOffUpdateFailed.type]: (state) => {
    setFailed(state.timeOffUpdate)
  }
})

export default calendarReducer

// PUBLIC ACTIONS

export const getCalendarBookings = (params) =>
  api({
    url: "/guide/calendar/bookings",
    params,
    onStart: calendarBookingRequested.type,
    onSuccess: calendarBookingReceived.type,
    onError: calendarBookingFailed.type
  })

export const getCalendarTripLocations = () =>
  api({
    url: "/guide/calendar/guide_trip_locations",
    onStart: calendarTripLocationsRequested.type,
    onSuccess: calendarTripLocationsReceived.type,
    onError: calendarTripLocationsFailed.type
  })

export const getCalendarJournalNote = (date) =>
  api({
    url: `/guide/journal_notes?date=${date}`,
    onStart: calendarJournalNoteRequested.type,
    onSuccess: calendarJournalNoteReceived.type,
    onError: calendarJournalNoteFailed.type
  })

export const createCalendarJournal = (data) =>
  api({
    url: "/guide/journal_notes",
    method: "post",
    onStart: calendarJournalCreateNoteRequested.type,
    onSuccess: calendarJournalCreateNoteReceived.type,
    onError: calendarJournalCreateNoteFailed.type,
    data
  })

export const updateCalendarJournal = (id, data) =>
  api({
    url: `/guide/journal_notes/${id}`,
    method: "put",
    data: data,
    onStart: calendarJournalUpdateNoteRequested.type,
    onSuccess: calendarJournalUpdateNoteReceived.type,
    onError: calendarJournalUpdateNoteFailed.type
  })

export const createCalendarTimeOff = (data, profileId, suppressErrorNotification = false) =>
  api({
    url: profileId ? `/guide/profiles/${profileId}/availabilities` : "/guide/availabilities",
    method: "post",
    onStart: calendarTimeOffCreateRequested.type,
    onSuccess: calendarTimeOffCreateReceived.type,
    onError: calendarTimeOffCreateFailed.type,
    suppressErrorNotification,
    data: { availability: data }
  })

export const getCalendarTimeOff = (params, profileId) =>
  api({
    url: profileId ? `/guide/profiles/${profileId}/availabilities` : "/guide/availabilities",
    params: { ...params },
    onStart: calendarTimeOffRequested.type,
    onSuccess: calendarTimeOffReceived.type,
    onError: calendarTimeOffFailed.type
  })

export const deleteTimeOff = (id, profileId) =>
  api({
    url: profileId ? `/guide/profiles/${profileId}/availabilities/${id}` : `/guide/availabilities/${id}`,
    method: "delete",
    onStart: calendarTimeOffDeleteRequested.type,
    onSuccess: calendarTimeOffDeleteReceived.type,
    onError: calendarTimeOffDeleteFailed.type
  })

export const updateCalendarTimeOff = (id, data, profileId, suppressErrorNotification = false) =>
  api({
    url: profileId ? `/guide/profiles/${profileId}/availabilities/${id}` : `/guide/availabilities/${id}`,
    method: "put",
    data: { availability: data },
    onStart: calendarTimeOffUpdateRequested.type,
    onSuccess: calendarTimeOffUpdateReceived.type,
    onError: calendarTimeOffUpdateFailed.type,
    suppressErrorNotification
  })

export const saveCalendarJournal = (id, data) => (id ? updateCalendarJournal(id, data) : createCalendarJournal(data))

export const getJournalVersions = (id, params = {}) =>
  api({
    url: `/guide/journal_notes/${id}/versions`,
    params: { ...params },
    onStart: calendarJournalVersionsRequested.type,
    onSuccess: calendarJournalVersionsReceived.type,
    onError: calendarJournalVersionsFailed.type
  })
